import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { MottagningsplatsMarkerDTO } from "~app/common/models/mottagningsplats/MottagningsplatsMarkerDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "../../../../common/services/api-client.service";
import { DeleteDestineringsomradeCommand } from "../commands/commands";
import {
    DeleteDestineringsomradeResultDto,
    Destineringsomrade,
    DestineringsomradeBasdataDto,
    HandelssortimentModel,
    SearchQuery
} from "../models/dso-models";

@Injectable({
    providedIn: "root"
})
export class DsoService {
    colors: string[];

    private readonly apiClient = inject(ApiClientService);

    deleteDestineringsomrade(command: DeleteDestineringsomradeCommand): Observable<DeleteDestineringsomradeResultDto> {
        return this.apiClient.post<DeleteDestineringsomradeCommand, DeleteDestineringsomradeResultDto>("destineringsomrade/delete", command);
    }

    getBasdata(): Observable<DestineringsomradeBasdataDto> {
        return this.apiClient.get<DestineringsomradeBasdataDto>("destineringsomrade/GetBasdata", false);
    }

    getColors(): Observable<string[]> {
        return this.apiClient.getCached<string[]>("transportforetag/colors", false).pipe(map((result) => {
            this.colors = result;
            return result;
        }));
    }

    getHandelssortiment(): Observable<HandelssortimentModel[]> {
        return this.apiClient.get<HandelssortimentModel[]>("handelssortiment?aktiv=true");
    }

    getMottagningsplatser(): Observable<MottagningsplatsMarkerDTO[]> {
        return this.apiClient.getCached<MottagningsplatsMarkerDTO[]>("mottagningsplats/markers", false);
    }

    getRandomColor(): string {
        // eslint-disable-next-line sonarjs/pseudo-random
        const rnd = Math.round(Math.random() * this.colors.length);
        return this.colors[rnd];
    }

    hexToRgb(color: string, opacity = 0.8): number[] {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
        return result ?
            [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16),
                opacity
            ] :
            null;
    }

    rgbToHex(rgb: any[]) {
        const r = this.componentToHex(rgb[0]);
        const g = this.componentToHex(rgb[1]);
        const b = this.componentToHex(rgb[2]);
        return `#${r}${g}${b}`;
    }

    search(query: SearchQuery): Observable<Destineringsomrade[]> {
        return this.apiClient.post<SearchQuery, Destineringsomrade[]>("destineringsomrade/Search", query, false);
    }

    searchForMottagningsplatsTypeAhead(input: string, kundordertyp: string): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();
        if (input.length < 3) {
            return subject.asObservable();
        }

        return this.apiClient
            .post<any, ITypeAheadResult[]>("typeahead/mottagningsplats", { input: input,
                kundordertyp: kundordertyp }, false, null, ApiClientService.JsonHeader);
    }

    private componentToHex(c) {
        const hex = c.toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    }
}
